export type Variant = 'a' | 'b' | 'c' | 'd';

export type VariantSource =
    | 'url-override'
    | 'remote-forced'
    | 'sticky-override'
    | 'session-random';

export interface AudioFeatureFlags {
    /** PR1516 — inject Opus DTX in offer/answer SDP. */
    opusDtx: boolean;
    /** PR1566 — inject full Opus tuning block (FEC, no-stereo, VBR, 32 kbps ceiling). */
    opusFullTuning: boolean;
    /** PR1516 — play low-volume connecting tone when inbound RTP stalls. */
    comfortTone: boolean;
    /** PR1516 — WebRTC connected/disconnected chimes. */
    lifecycleChimes: boolean;
    /** PR1516 — soft WS reconnect + ICE restart on network change. */
    networkChangeHandler: boolean;
    /** PR1516 — periodic pc.getStats() polling with stuck detection. */
    audioHealthMonitor: boolean;
    /** PR1516 — dynamic ICE server fetch with cache + prefetch. */
    iceServersCacheAndPrefetch: boolean;
    /** PR1566 — force-clear ICE cache before answering inbound calls. */
    clearIceCacheBeforeAnswer: boolean;
    /** PR1566 — on-demand health poll when tab becomes visible. */
    tabVisibilityHandler: boolean;
    /** PR1566 — setParameters networkPriority:'high' on audio sender. */
    audioSenderPriority: boolean;
    /** PR1566 — spread navigator.connection fields into audio_* logs. */
    networkTypeLogging: boolean;
    /** Mobile PR283 2ab7953 — bump Opus maxaveragebitrate 32000 → 48000 for HD P2P voice. */
    opusHdBitrate: boolean;
    /** Mobile PR283 0a31207 — inject `a=rtcp-fb:<opus-pt> nack` in offer (+ answer when remote offered). */
    opusNack: boolean;
    /** Mobile PR283 0a31207/2e540b2 — RTCRtpReceiver.jitterBufferTarget=120ms, deferred to PC 'connected'. */
    receiverJitterTarget: boolean;
    /** Mobile PR283 7529e26 — 8s sustained-loss grace before ICE restart on WS disconnect. */
    wsSustainedLossGrace: boolean;
    /**
     * CU-869dafa5q — route the remote MediaStream through AudioWorklet rather than
     * binding directly to `audioRef.srcObject`. AudioWorklet runs on a dedicated audio
     * render thread that's not subject to background-tab throttling — the only
     * mechanism among the variant-d candidates whose CAUSE matches the chopping
     * SYMPTOM users report.
     */
    backgroundedAudioWorklet: boolean;
}

export interface RemoteAbConfig {
    audio?: {
        force?: Variant | null;
    };
}

export const VARIANT_STORAGE_KEY = 'phq_ab_variant';

const FLAG_MATRIX: Record<Variant, AudioFeatureFlags> = {
    a: {
        opusDtx: false, opusFullTuning: false, comfortTone: false, lifecycleChimes: false,
        networkChangeHandler: false, audioHealthMonitor: false, iceServersCacheAndPrefetch: false,
        clearIceCacheBeforeAnswer: false, tabVisibilityHandler: false, audioSenderPriority: false,
        networkTypeLogging: false, opusHdBitrate: false, opusNack: false,
        receiverJitterTarget: false, wsSustainedLossGrace: false,
        backgroundedAudioWorklet: false,
    },
    b: {
        opusDtx: true, opusFullTuning: false, comfortTone: true, lifecycleChimes: true,
        networkChangeHandler: true, audioHealthMonitor: true, iceServersCacheAndPrefetch: true,
        clearIceCacheBeforeAnswer: false, tabVisibilityHandler: false, audioSenderPriority: false,
        networkTypeLogging: false, opusHdBitrate: false, opusNack: false,
        receiverJitterTarget: false, wsSustainedLossGrace: false,
        backgroundedAudioWorklet: false,
    },
    c: {
        opusDtx: true, opusFullTuning: true, comfortTone: true, lifecycleChimes: true,
        networkChangeHandler: true, audioHealthMonitor: true, iceServersCacheAndPrefetch: true,
        clearIceCacheBeforeAnswer: true, tabVisibilityHandler: true, audioSenderPriority: true,
        networkTypeLogging: true, opusHdBitrate: true, opusNack: true,
        receiverJitterTarget: true, wsSustainedLossGrace: true,
        backgroundedAudioWorklet: false,
    },
    // d = c (the previous most-robust baseline) + three backgrounded-tab
    // mitigations stacked. Each is feature-detected at the call site so a
    // browser missing the underlying API (AudioWorklet, Insertable Streams)
    // falls back to the c-level behaviour without dropping the call.
    // CU-869dafa5q — Chrome OS users running calls in backgrounded tabs.
    d: {
        opusDtx: true, opusFullTuning: true, comfortTone: true, lifecycleChimes: true,
        networkChangeHandler: true, audioHealthMonitor: true, iceServersCacheAndPrefetch: true,
        clearIceCacheBeforeAnswer: true, tabVisibilityHandler: true, audioSenderPriority: true,
        networkTypeLogging: true, opusHdBitrate: true, opusNack: true,
        receiverJitterTarget: true, wsSustainedLossGrace: true,
        backgroundedAudioWorklet: true,
    },
};

const isVariant = (v: unknown): v is Variant => v === 'a' || v === 'b' || v === 'c' || v === 'd';

let cached: { variant: Variant; source: VariantSource } | null = null;

const safeLocalStorage = (): Storage | null => {
    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

function doResolve(remote: RemoteAbConfig): { variant: Variant; source: VariantSource } {
    const ls = safeLocalStorage();

    // 1. ?ver=a|b|c (sticky) or ?ver=auto (clear stickiness)
    let urlOverride: Variant | null = null;
    let urlRequestedAuto = false;
    try {
        const raw = new URLSearchParams(window.location.search).get('ver');
        if (raw) {
            const v = raw.toLowerCase();
            if (isVariant(v)) {
                urlOverride = v;
            } else if (v === 'auto') {
                urlRequestedAuto = true;
            }
            // anything else: ignored
        }
    } catch {
        // malformed URL — ignore
    }

    if (urlOverride) {
        try {
            ls?.setItem(VARIANT_STORAGE_KEY, urlOverride);
        } catch {
            // private-mode quota/access — still honour the URL value in memory
        }
        return { variant: urlOverride, source: 'url-override' };
    }

    if (urlRequestedAuto) {
        try {
            ls?.removeItem(VARIANT_STORAGE_KEY);
        } catch {
            // ignore
        }
        // fall through — skip sticky lookup
    }

    // 2. Remote fleet force from /ab-config.json
    const forced = remote.audio?.force;
    if (isVariant(forced)) {
        return { variant: forced, source: 'remote-forced' };
    }

    // 3. Sticky override from a previous ?ver=a|b|c visit
    if (!urlRequestedAuto) {
        try {
            const stored = ls?.getItem(VARIANT_STORAGE_KEY) ?? null;
            if (isVariant(stored)) {
                return { variant: stored, source: 'sticky-override' };
            }
        } catch {
            // ignore
        }
    }

    // 4. Per-page-load random — not persisted. Random split is c-vs-d only:
    // c is the previous most-robust baseline; d layers AudioWorklet +
    // Insertable-Streams + silent-audio-keepalive on top of c for backgrounded
    // tabs. a and b are intentionally NOT in the random pool — they remain
    // available via ?ver= or remote.audio.force as control branches for
    // diagnostic A/B comparisons.
    const variant: Variant = Math.random() < 0.5 ? 'c' : 'd';
    return { variant, source: 'session-random' };
}

/**
 * Resolve and cache the variant for the lifetime of this page load.
 * Idempotent — subsequent calls (with or without a `remote` arg) return the
 * cached value. Safe to call multiple times.
 */
export function resolveVariant(remote: RemoteAbConfig = {}): { variant: Variant; source: VariantSource } {
    if (cached) return cached;
    try {
        cached = doResolve(remote);
    } catch {
        cached = { variant: 'c', source: 'session-random' };
    }
    return cached;
}

/**
 * Synchronous variant read. Returns `null` if `resolveVariant()` hasn't been
 * called yet — do NOT auto-resolve here. The logger imports this module and
 * fires a first log entry at import time (well before `main.tsx` has a chance
 * to fetch `/ab-config.json`); auto-resolving would lock in a variant without
 * the remote fleet-force in scope and ignore `audio.force` for the whole page
 * load. Early log lines therefore carry no `variant` field, which is a better
 * failure mode than silently ignoring a fleet-wide rollback.
 */
export function getVariant(): Variant | null {
    return cached?.variant ?? null;
}

/**
 * Feature flags for the active variant. If the variant hasn't been resolved
 * yet (rare — pre-`resolveVariant()` consumers) default to variant C flags,
 * i.e. current HEAD behaviour. Every runtime consumer (`sipService`,
 * `audioHealthMonitor`, `iceServers`) runs only after `main.tsx` has resolved,
 * so the default is a belt-and-braces guard.
 */
export function getAudioFlags(): AudioFeatureFlags {
    const v = cached?.variant;
    const flags = FLAG_MATRIX[v ?? 'c'];
    if (flags.backgroundedAudioWorklet && isNonChromeOsChromium()) {
        return { ...flags, backgroundedAudioWorklet: false };
    }
    return flags;
}

function isNonChromeOsChromium(): boolean {
    try {
        if (typeof navigator === 'undefined') return false;
        const ua = navigator.userAgent;
        if (/CrOS/i.test(ua)) return false;
        return /Chrome|Chromium|Edg|OPR/i.test(ua);
    } catch {
        return false;
    }
}

/** Test-only: wipe the cached singleton so the next `resolveVariant` re-runs. */
export function __resetVariantForTest(): void {
    cached = null;
}
